/**
 * EndorseDialog — opens from a match card, lets the member pick qualities
 * to endorse the other member for. Optional short note.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGiveEndorsement } from "@/lib/endorsements/useEndorsements";
import { qualitiesFor } from "@/lib/endorsements/qualities";
import { useProfile } from "@/lib/profile/useProfile";
import type { AppId } from "@/lib/apps";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiverId: string;
  receiverName: string;
  matchId?: string | null;
  app?: AppId | null;
}

const EndorseDialog = ({
  open,
  onOpenChange,
  receiverId,
  receiverName,
  matchId = null,
  app = null,
}: Props) => {
  const { profile } = useProfile();
  const { give } = useGiveEndorsement();
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const choices = qualitiesFor(app ?? (profile?.active_app as AppId | null));

  const toggle = (q: string) => {
    setPicked((prev) => (prev.includes(q) ? prev.filter((x) => x !== q) : [...prev, q]));
  };

  const submit = async () => {
    if (picked.length === 0) {
      toast.error("Pick at least one quality.");
      return;
    }
    setBusy(true);
    const res = await give(receiverId, picked, { matchId, note: note.trim() || null });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error ?? "Couldn't save endorsement.");
      return;
    }
    toast.success(`Endorsed ${receiverName}.`);
    setPicked([]);
    setNote("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-primary" aria-hidden />
            Endorse {receiverName}
          </DialogTitle>
          <DialogDescription>
            After meeting in person, share what stood out. Endorsements appear on their profile.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
          {choices.map((q) => {
            const active = picked.includes(q);
            return (
              <button
                key={q}
                type="button"
                onClick={() => toggle(q)}
                className={`text-left rounded-xl border px-3 py-2 text-sm transition-colors ${
                  active
                    ? "border-foreground bg-secondary"
                    : "border-border hover:bg-secondary/50"
                }`}
              >
                {q}
              </button>
            );
          })}
        </div>

        <Textarea
          rows={2}
          maxLength={280}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (private to them)…"
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || picked.length === 0}>
            {busy ? "Saving…" : `Endorse (${picked.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EndorseDialog;
